import { uid } from './workoutSession';
import {
  distanceInputFromMeters,
  metersFromDistanceInput,
  parseMmSsToSeconds,
  secondsToMmSs,
  type IntervalPrescription,
  type ZoneMinutes,
  type ZoneModel,
} from './cardioTracking';
import type { CardioSegment, Workout } from '../types';

export type DraftLap = {
  key: string;
  kind: 'work' | 'rest' | 'lap';
  duration: string;
  distance: string;
  avgHr: string;
};

export type DraftSegment = {
  key: string;
  activityType: string;
  isTransition: boolean;
  durationMinutes: string;
  distance: string;
  avgHr: string;
  maxHr: string;
  elevGain: string;
  elevLoss: string;
  cadence: string;
  rpe: string;
  zoneModel: ZoneModel;
  z1: string;
  z2: string;
  z3: string;
  z4: string;
  z5: string;
  intervalReps: string;
  workDistance: string;
  workTime: string;
  targetPace: string;
  restSeconds: string;
  showSplits: boolean;
  laps: DraftLap[];
  poolLength: string;
  stroke: string;
  swolf: string;
  avgPower: string;
  maxPower: string;
  np: string;
  tss: string;
};

export function emptyLap(kind: DraftLap['kind'] = 'lap'): DraftLap {
  return { key: uid(), kind, duration: '', distance: '', avgHr: '' };
}

export function emptySegment(activityType: string, isTransition = false): DraftSegment {
  return {
    key: uid(),
    activityType,
    isTransition,
    durationMinutes: '',
    distance: '',
    avgHr: '',
    maxHr: '',
    elevGain: '',
    elevLoss: '',
    cadence: '',
    rpe: '',
    zoneModel: 'max_hr',
    z1: '',
    z2: '',
    z3: '',
    z4: '',
    z5: '',
    intervalReps: '',
    workDistance: '',
    workTime: '',
    targetPace: '',
    restSeconds: '',
    showSplits: false,
    laps: [],
    poolLength: '',
    stroke: '',
    swolf: '',
    avgPower: '',
    maxPower: '',
    np: '',
    tss: '',
  };
}

function numStr(n: number | null | undefined): string {
  return n != null && Number.isFinite(n) ? String(n) : '';
}

export function draftFromSegment(seg: CardioSegment, imperial: boolean): DraftSegment {
  const zm = (seg.zoneMinutes ?? {}) as ZoneMinutes;
  const rx = seg.intervalPrescription;
  return {
    ...emptySegment(seg.activityType, seg.isTransition),
    durationMinutes:
      seg.durationSeconds != null && seg.durationSeconds > 0
        ? String(Math.round(seg.durationSeconds / 60))
        : '',
    distance: distanceInputFromMeters(seg.distanceMeters, imperial),
    avgHr: numStr(seg.avgHr),
    maxHr: numStr(seg.maxHr),
    elevGain: numStr(seg.elevGainM),
    elevLoss: numStr(seg.elevLossM),
    cadence: numStr(seg.cadence),
    rpe: numStr(seg.rpe),
    zoneModel: (seg.zoneModel as ZoneModel) || 'max_hr',
    z1: numStr(zm.z1),
    z2: numStr(zm.z2),
    z3: numStr(zm.z3),
    z4: numStr(zm.z4),
    z5: numStr(zm.z5),
    intervalReps: rx?.reps ? String(rx.reps) : '',
    workDistance: distanceInputFromMeters(rx?.workDistanceM, imperial),
    workTime: secondsToMmSs(rx?.workSeconds),
    targetPace: rx?.targetPaceSecPerKm
      ? secondsToMmSs(imperial ? rx.targetPaceSecPerKm * 1.60934 : rx.targetPaceSecPerKm)
      : '',
    restSeconds: numStr(rx?.restSeconds),
    showSplits: (seg.laps?.length ?? 0) > 0,
    laps: (seg.laps ?? []).map((l) => ({
      key: uid(),
      kind: l.kind === 'work' || l.kind === 'rest' ? l.kind : 'lap',
      duration: secondsToMmSs(l.durationSeconds) || numStr(l.durationSeconds),
      distance: distanceInputFromMeters(l.distanceMeters, imperial),
      avgHr: numStr(l.avgHr),
    })),
    poolLength: numStr(seg.poolLengthM),
    stroke: seg.stroke ?? '',
    swolf: numStr(seg.swolf),
    avgPower: numStr(seg.avgPowerW),
    maxPower: numStr(seg.maxPowerW),
    np: numStr(seg.normalizedPowerW),
    tss: numStr(seg.tss),
  };
}

export function draftsFromWorkout(workout: Workout, imperial: boolean): DraftSegment[] {
  const segs = workout.cardioSegments ?? [];
  if (segs.length === 0) {
    const d = emptySegment(workout.activityType ?? 'outdoor_run');
    d.durationMinutes = workout.durationMinutes ? String(workout.durationMinutes) : '';
    return [d];
  }
  return segs.map((s) => draftFromSegment(s, imperial));
}

function optNum(s: string): number | null {
  const n = Number(s);
  return Number.isFinite(n) && s.trim() !== '' ? n : null;
}

export function segmentToPayload(
  d: DraftSegment,
  imperial: boolean,
  fallbackMinutes: number | null,
  sortOrder: number,
) {
  const durationMinutes = optNum(d.durationMinutes) ?? (d.isTransition ? null : fallbackMinutes);
  const durationSeconds =
    durationMinutes != null && durationMinutes > 0 ? Math.round(durationMinutes * 60) : null;
  const workSeconds = parseMmSsToSeconds(d.workTime);
  const targetPaceLocal = parseMmSsToSeconds(d.targetPace);
  const prescription: IntervalPrescription | null =
    optNum(d.intervalReps) && optNum(d.intervalReps)! >= 1
      ? {
          reps: Math.round(optNum(d.intervalReps)!),
          workDistanceM: metersFromDistanceInput(d.workDistance, imperial),
          workSeconds,
          targetPaceSecPerKm:
            targetPaceLocal != null ? (imperial ? targetPaceLocal / 1.60934 : targetPaceLocal) : null,
          restSeconds: optNum(d.restSeconds),
        }
      : null;
  const zoneMinutes: ZoneMinutes = {};
  const z1 = optNum(d.z1);
  const z2 = optNum(d.z2);
  const z3 = optNum(d.z3);
  const z4 = optNum(d.z4);
  const z5 = optNum(d.z5);
  if (z1) zoneMinutes.z1 = z1;
  if (z2) zoneMinutes.z2 = z2;
  if (z3) zoneMinutes.z3 = z3;
  if (z4) zoneMinutes.z4 = z4;
  if (z5) zoneMinutes.z5 = z5;
  return {
    activityType: d.activityType,
    sortOrder,
    isTransition: d.isTransition,
    durationSeconds,
    distanceMeters: metersFromDistanceInput(d.distance, imperial),
    elevGainM: optNum(d.elevGain),
    elevLossM: optNum(d.elevLoss),
    avgHr: optNum(d.avgHr) != null ? Math.round(optNum(d.avgHr)!) : null,
    maxHr: optNum(d.maxHr) != null ? Math.round(optNum(d.maxHr)!) : null,
    cadence: optNum(d.cadence),
    rpe: optNum(d.rpe),
    poolLengthM: optNum(d.poolLength),
    stroke: d.stroke.trim() || null,
    swolf: optNum(d.swolf),
    avgPowerW: optNum(d.avgPower),
    maxPowerW: optNum(d.maxPower),
    normalizedPowerW: optNum(d.np),
    tss: optNum(d.tss),
    zoneModel: d.zoneModel,
    zoneMinutes: Object.keys(zoneMinutes).length ? zoneMinutes : null,
    intervalPrescription: prescription,
    laps: d.laps.map((l, i) => ({
      sortOrder: i,
      kind: l.kind,
      durationSeconds: parseMmSsToSeconds(l.duration),
      distanceMeters: metersFromDistanceInput(l.distance, imperial),
      avgHr: optNum(l.avgHr),
    })),
  };
}

export function formatIntervalLine(rx: IntervalPrescription | null | undefined, imperial: boolean): string | null {
  if (!rx?.reps) return null;
  const dist =
    rx.workDistanceM != null
      ? rx.workDistanceM >= 1000
        ? imperial
          ? `${Math.round((rx.workDistanceM / 1609.34) * 100) / 100} mi`
          : `${Math.round(rx.workDistanceM)} m`
        : `${Math.round(rx.workDistanceM)} m`
      : null;
  const work = dist ?? (rx.workSeconds != null ? secondsToMmSs(rx.workSeconds) : null);
  const pace =
    rx.targetPaceSecPerKm != null
      ? `@ ${secondsToMmSs(imperial ? rx.targetPaceSecPerKm * 1.60934 : rx.targetPaceSecPerKm)}`
      : '';
  const rest = rx.restSeconds != null ? `, ${rx.restSeconds}s rest` : '';
  return `${rx.reps}×${work ?? '?'}${pace}${rest}`;
}

export function formatDistanceM(meters: number | null | undefined, imperial: boolean): string | null {
  if (meters == null || !Number.isFinite(meters) || meters <= 0) return null;
  return imperial ? `${(meters / 1609.34).toFixed(2)} mi` : `${(meters / 1000).toFixed(2)} km`;
}

export function primaryDistanceM(workout: Workout): number | null {
  const segs = workout.cardioSegments ?? [];
  const sum = segs.reduce((s, x) => s + (x.isTransition ? 0 : x.distanceMeters ?? 0), 0);
  return sum > 0 ? sum : null;
}
